import { Group, NumberInput } from "@mantine/core"
import { DefaultInfiniteList } from "../../../../-components/InfiniteList"
import { DefaultListItem } from "../../../../-components/default-list/list-item"
import { useSearch, useNavigate } from "@tanstack/react-router"
import { HideableFilterGroup } from "../../-components/hideable-filter-group"
import { SearchTextInput } from "../../-components/search-text-input"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { useDisclosure } from "@mantine/hooks"
import useGetResourceFieldTypes from "./useGetResourceFieldTypes"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import {
  FieldFilterPill,
  AddFieldFilterModal,
  getUpdatedFilterDict,
  FieldFilterButton,
} from "../../../../-components/field-data"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useTranslation } from "react-i18next"
import { useDebouncedObject } from "@/utils/use-debounced-object"
import { ResourceCard } from "./ResourceCard"
import {
  FieldData,
  ResourceFilter,
  ResourcePageRequest,
} from "@/orval/gen/model"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { getPaginatedResources } from "@/orval/gen"

export function ResourcesCardList({
  category,
}: {
  category: ResourceTypeCategory
}) {
  const search = useSearch({ from: "/app/resources/resources" })
  const { filter } = search
  const navigate = useNavigate()
  const [visible, setVisible] = useUserLocalStorage<boolean>({
    key: "resources-filter-visibility",
    defaultValue: true,
  })
  const { data: fieldTypes } = useGetResourceFieldTypes(category)
  const [modalOpened, { open, close }] = useDisclosure()
  const { t } = useTranslation("app_server")

  const updateFilter = (newValue: ResourceFilter) =>
    navigate({
      to: "/app/resources/resources",
      search: { filter: { ...filter, ...newValue } },
      replace: true,
    })
  const updateSearch = (newValue: Partial<typeof search>) => {
    navigate({
      to: "/app/resources/resources",
      search: { ...search, ...newValue },
      replace: true,
    })
  }

  React.useEffect(() => {
    updateFilter({ resourceTypeTreeNodes: undefined })
  }, [category])

  return (
    <>
      <Group pb={5} justify="space-between">
        <Group>
          <SearchTextInput
            autoFocus
            value={search.globalFilter ?? undefined}
            onChange={(e) => updateSearch({ globalFilter: e.target.value })}
          />
          <HideableFilterGroup align="end" visible={visible}>
            <ResourceTypeTreeSelect
              category={category}
              miw={250}
              label={t("Type")}
              onCheckedStateChange={(values) =>
                updateFilter({ resourceTypeTreeNodes: values })
              }
              initialCheckedState={filter?.resourceTypeTreeNodes ?? []}
            />
            <NumberInput
              label={`${t("Available")}/${t("Amount")}`}
              value={filter?.availableCount ?? undefined}
              onChange={(value) =>
                typeof value === "number"
                  ? updateFilter({ availableCount: value })
                  : updateFilter({ availableCount: undefined })
              }
            />
            {fieldTypes.length > 0 && <FieldFilterButton onClick={open} />}
            {fieldTypes.map(
              (type) =>
                !!filter?.fieldFilter?.[type.id] && (
                  <FieldFilterPill
                    typeName={type.name}
                    filter={filter.fieldFilter[type.id]}
                    onRemove={() => {
                      if (filter.fieldFilter) {
                        const { [type.id]: _, ...rest } = filter.fieldFilter

                        updateFilter({ fieldFilter: rest })
                      }
                    }}
                  />
                )
            )}
          </HideableFilterGroup>
        </Group>
        <HideableFilterGroup.Button
          visible={visible}
          onChange={(e) => setVisible(!e)}
          activeFilters={
            Object.keys(filter?.fieldFilter ?? {}).length +
            (filter?.resourceTypeTreeNodes?.length ? 1 : 0)
          }
        />
      </Group>
      <SuspenseWithErrorBoundary>
        <List
          request={{
            ...search,
            filter: { ...filter, category },
          }}
        />
      </SuspenseWithErrorBoundary>
      <AddFieldFilterModal
        opened={modalOpened}
        onClose={close}
        fieldTypes={fieldTypes}
        onAdd={(record) => {
          updateFilter({
            fieldFilter: getUpdatedFilterDict(
              record.type,
              record.value,
              filter?.fieldFilter
            ),
          })
        }}
        values={filter?.fieldFilter}
      />
    </>
  )
}

function List({
  request,
}: {
  request: Omit<ResourcePageRequest, "pageSize" | "pageParam">
}) {
  const navigate = useNavigate()
  const [debouncedRequest] = useDebouncedObject(request, 500)
  const query = useSuspenseInfiniteQuery({
    queryKey: ["getPaginatedResources", debouncedRequest],
    queryFn: ({ pageParam }) =>
      getPaginatedResources({ ...debouncedRequest, pageParam, pageSize: 20 }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasNextPage ? allPages.length + 1 : undefined,
    initialPageParam: 0,
  })
  const [expanded, setExpanded] = React.useState(false)
  const { t } = useTranslation("app_server")
  return (
    <DefaultInfiniteList
      w={"100%"}
      justify={"space-evenly"}
      queryResult={query}
      renderItem={({ entity }) => (
        <DefaultListItem
          maw={"100%"}
          onClick={() =>
            navigate({
              to: "/app/resources/$id",
              params: { id: entity.resourceId.toString() },
            })
          }
        >
          <ResourceCard
            resourceId={entity.resourceId}
            expanded={expanded}
            onExpandedChange={setExpanded}
            title={[entity.resourceType, entity.name].join(" / ")}
            attachments={entity.attachments}
            defaultFields={[
              {
                label: t("Available"),
                value: entity.availableCount + "/" + entity.count,
              },
            ]}
            fieldData={
              entity.resourceFields
                .filter((rf) => !!rf.value)
                .flatMap((f) => f.value)
                .filter((f) => !!f) as FieldData[]
            }
          />
        </DefaultListItem>
      )}
      direction={"row"}
      wrap={"wrap"}
    />
  )
}
